import {
  Button,
  CardMedia,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import Title from "../../components/Title";
import axios from "../../MyAxios";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MentionsInput, Mention } from "react-mentions";
import mentionStyle from "./mentionStyles";
import mentionsInputStyle from "./mentionsInputStyles";
import {
  ADD_COMMUNITY_CONNECTION,
  CREATE_POST,
  GET_ALL_USERS,
  ROOT,
  UPLOAD_FILE,
  GET_GROUP,
  CHECK_GROUP_DISABLED_FOR_EVENTS,
} from "../../constants/api";
import {
  addAllToConnections,
  addAllToGroups,
  getLinkMetadata,
  resetPost,
  setAgeRestricted,
  setDescription,
  setFile,
  setFileName,
  setMedia,
  setTags,
  setTitle,
} from "../../redux/postSlice";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import Layout from "../../Layout";
import upload from "../../assets/img/upload.svg";
import useLoggedIn from "../../hooks/useLoggedIn";
import { addToPosts, setTotalPages, updatePost } from "@redux/prevPostsSlice";
import MyAvatar from "@components/admin/MyAvatar";
import EmojiSelector from "@components/emoji-selector/EmojiSelector";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardIos";
import AddComCon from "./AddComCon";
import SelectTags from "./SelectTags";
import Loader from "@components/Loader";
import OpenGraphInfo from "@components/OpenGraphInfo";
import { Close as CloseIcon } from "@mui/icons-material";
import validator from "validator";
import PostTypeTabs from "../../components/PostTypeTabs.js";

const useStyles = makeStyles((theme) => ({
  border: { borderWidth: 2, borderColor: "#887E7E" },
  borderGreen: { borderWidth: 1, borderColor: "#51CDA0" },
  textColor: { color: theme.palette.primary.main },
  button: { backgroundColor: theme.palette.primary.main, color: "#fff" },
  fontSmall: { fontSize: "10px" },
  bottomImg: { position: "fixed", bottom: 0, right: 0, left: 0 },
  colorGreen: { backgroundColor: "#e5f8f1" },
  bgGrey: { backgroundColor: "#efefef" },
  bgGreen: { backgroundColor: "#e5f8f1" },
  textGrey: { color: "#626262" },
}));

const CreatePost = (res) => {
  useLoggedIn();

  const classes = useStyles();
  const dispatch = useDispatch();
  const navigation = useNavigate();

  const { id } = useParams();
  const location = useLocation();
  const fileInput = useRef(null);
  const editPostFormData = useRef(new FormData());

  const {
    description,
    file,
    fileName,
    media,
    isGroupOnlyPost,
    ageRestricted,
    connections,
    selectedGroups,
    selectedPostCommunities,
    communities,
    selectedTags,
  } = useSelector((state) => state.post);
  const { userRecord } = useSelector((state) => state.user);

  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionSuggestionsLoading, setMentionSuggestionsLoading] =
    useState(false);
  const [prevFile, setPrevFile] = useState();
  const [prevFileType, setPrevFileType] = useState();
  const [loading, setLoading] = useState(false);
  const [isGroupDisabledForEvents, setIsGroupDisabledForEvents] =
    useState(false);

  const [titleCursorPos, setTitleCursorPos] = useState(0);
  const [descCursorPos, setDescCursorPos] = useState(0);

  const [showComCon, setShowComCon] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [showEventTab, setShowEventTab] = useState(false);

  const [postLinkPreview, setPostLinkPreview] = useState();
  const [postLinkPreviewError, setPostLinkPreviewError] = useState("");
  const [postLinkPreviewLoading, setPostLinkPreviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("standard");
  const [submitted, setSubmitted] = useState(false);
  // firstLinkRef: locks the first URL typed — only it ever gets OG treatment
  // lastFetchedLinkRef: dedup guard so we don't re-fetch the same URL twice
  const lastFetchedLinkRef = useRef(null);
  const firstLinkRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm();

  useEffect(() => {
    register("description", {
      validate: (v) =>
        (v && String(v).trim() !== "") || "Description is required.",
    });
  }, [register]);

  // ── URL extractor ──────────────────────────────────────────────────────────
  const extractUrl = (text) => {
    if (!text) return null;
    let t = String(text).trim();
    try {
      t = decodeURIComponent(t);
    } catch (e) {}
    t = t.replace(/^<|>$/g, "");

    const httpRegex = /https?:\/\/[^\s<>'")]+/i;
    const wwwRegex = /www\.[^\s<>'")]+/i;
    const domainRegex =
      /(?:https?:\/\/)?(?:www\.)?[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?:\/[^\s<>'"]*)?/i;

    let match = t.match(httpRegex) || t.match(wwwRegex) || t.match(domainRegex);
    if (!match) return null;

    let link = match[0].replace(/[.,;:!?')\]]+$/g, "");
    if (!/^https?:\/\//i.test(link)) link = "https://" + link;

    try {
      return new URL(link).toString();
    } catch (e) {
      return null;
    }
  };

  // ── OG fetch — ONLY called from onKeyDown (Space/Enter) and onBlur ─────────
  // Never runs on every keystroke. e.target.value is used directly so there
  // is no stale-closure risk from the description redux state.

  // Decode HTML entities (e.g. &#x27; → ', &amp; → &) that OG scrapers return
  const decodeHtmlEntities = (str) => {
    if (!str || typeof str !== "string") return str;
    try {
      const txt = document.createElement("textarea");
      txt.innerHTML = str;
      return txt.value;
    } catch (e) {
      return str;
    }
  };

  const decodeMetadata = (metadata) => {
    if (!metadata || typeof metadata !== "object") return metadata;
    const decoded = {};
    for (const [key, val] of Object.entries(metadata)) {
      decoded[key] = typeof val === "string" ? decodeHtmlEntities(val) : val;
    }
    return decoded;
  };

  const tryFetchOG = (currentValue) => {
    if (watch("hideLinkPreview")) return;

    const extracted = extractUrl(currentValue || "");

    // ✅ FIX: no URL in text — clear error and bail
    if (!extracted) {
      setPostLinkPreviewError("");
      return;
    }

    // ✅ FIX: URL changed from the locked one — reset locks to allow new fetch
    if (firstLinkRef.current && extracted !== firstLinkRef.current) {
      firstLinkRef.current = null;
      lastFetchedLinkRef.current = null;
      setPostLinkPreview(undefined);
      setPostLinkPreviewError("");
    }

    // Already fetched this exact URL — skip
    if (extracted === lastFetchedLinkRef.current) return;

    if (!validator.isURL(extracted)) {
      setPostLinkPreviewError("Invalid URL in description.");
      return;
    }

    // ✅ FIX: valid URL — clear any stale error before fetching
    setPostLinkPreviewError("");

    firstLinkRef.current = extracted;
    lastFetchedLinkRef.current = extracted;
    setPostLinkPreviewLoading(true);
    setValue(extracted);
    resetFile();

    dispatch(
      getLinkMetadata({
        link: extracted,
        onSuccess: (metadata) => {
          const meta = decodeMetadata(metadata);
          if (!meta?.ogDescription && (!meta?.ogImage || meta.ogImage.length === 0)) {
            setPostLinkPreviewError("Please double-check the url and try again.");
            return;
          }
          setPostLinkPreview({ sourceLink: extracted, ...meta });
          if (meta?.ogDescription) {
            const currentDesc = (currentValue || "").trim();

            // Keep the URL (and any other text) as-is, append OG description after
            const newDesc = currentDesc
              ? `${currentDesc}\n\n${meta.ogDescription}`
              : meta.ogDescription;

            dispatch(setDescription(newDesc));
            setValue("description", newDesc);
          }
        },
        onError: (error) => setPostLinkPreviewError(error),
      }),
    ).finally(() => setPostLinkPreviewLoading(false));
  };

  // ── postLink field handler (separate link input) ───────────────────────────
  const handleEnterPostLink = () => {
    // ✅ FIX: field cleared — reset everything cleanly
    if (!watch("postLink")) {
      setPostLinkPreview(undefined);
      setPostLinkPreviewError(""); // ✅ FIX: clear error
      firstLinkRef.current = null;
      lastFetchedLinkRef.current = null;
      return;
    }

    let payloadLink = watch("postLink")?.trim();
    payloadLink = extractUrl(payloadLink);

    if (!payloadLink) {
      setPostLinkPreviewError("Invalid URL in post link.");
      return;
    }

    if (watch("hideLinkPreview")) return;

    // ✅ FIX: valid URL — clear stale error
    setPostLinkPreviewError("");

    resetFile();
    setPostLinkPreview();
    setValue("postLink", payloadLink);

    // ✅ FIX: reset locks so the new link can be fetched even if different from previous
    firstLinkRef.current = payloadLink;
    lastFetchedLinkRef.current = payloadLink;

    setPostLinkPreviewLoading(true);
    dispatch(
      getLinkMetadata({
        link: payloadLink,
        onSuccess: (metadata) => {
          if (!metadata?.ogDescription && (!metadata?.ogImage || metadata.ogImage.length === 0)) {
            setPostLinkPreviewError("Please double-check the url and try again.");
            return;
          }
          setPostLinkPreview({ sourceLink: payloadLink, ...metadata });
          if (metadata?.ogDescription) {
            setValue("description", metadata?.ogDescription);
            dispatch(setDescription(metadata?.ogDescription));
          }
        },
        onError: (error) => setPostLinkPreviewError(error),
      }),
    ).then(() => setPostLinkPreviewLoading(false));
  };

  // ── Sync form values ───────────────────────────────────────────────────────
  useEffect(() => {
    setValue("description", description || "");
    setValue("mediaId", media || "");
  }, [description, setValue, file, media]);

  useEffect(() => {
    if (submitted) {
      if (!description || String(description).trim() === "") {
        // Force validation to show error
        register("description", {
          validate: (v) =>
            (v && String(v).trim() !== "") || "Description is required.",
        });
      }
    }
  }, [submitted, description, register]);

  useEffect(() => {
    if (file) {
      setPrevFile(URL.createObjectURL(file));
      setPrevFileType(file?.type?.split("/")?.[0]);
    }
  }, [file]);

  // ── Mentions only — OG is NOT triggered here ───────────────────────────────
  useEffect(() => {
    if (!description) {
      firstLinkRef.current = null;
      lastFetchedLinkRef.current = null;
      setPostLinkPreview(undefined);
      setPostLinkPreviewError(""); // ✅ FIX: clear error when description cleared
      return;
    }

    // ✅ FIX: if the URL in description changed, reset locks so new URL can be fetched
    const currentUrl = extractUrl(description);
    if (
      firstLinkRef.current &&
      currentUrl &&
      currentUrl !== firstLinkRef.current
    ) {
      firstLinkRef.current = null;
      lastFetchedLinkRef.current = null;
      setPostLinkPreview(undefined);
      setPostLinkPreviewError("");
    }

    let mentionStrings = description.match(/\B@[a-z0-9_-]+/gi);
    setMentionSuggestions([]);
    if (mentionStrings?.length) {
      mentionStrings = mentionStrings.map((str) => str.slice(1));
      setMentionSuggestionsLoading(true);
      axios
        .get("/user/get-user-by-usernames", {
          params: { usernames: mentionStrings },
        })
        .then((response) => setMentionSuggestions(response.data))
        .finally(() => setMentionSuggestionsLoading(false));
    }
  }, [description]);

  // ── Load existing post for editing ────────────────────────────────────────
  useEffect(() => {
    if (id) {
      axios.get(`/post/${id}`).then((res) => {
        const post = res.data;
        dispatch(setDescription(post.description ?? ""));
        dispatch(setAgeRestricted(post.ageRestricted));
        dispatch(setTags(post.tags ?? []));
        dispatch(addAllToConnections(post.connections?.map((x) => x.id) ?? []));
        dispatch(addAllToGroups(post.groups?.map((x) => x.id) ?? []));
        if (post.postLink) setValue("postLink", post.postLink);
        if (post.postLinkMetadata) {
          const meta =
            typeof post.postLinkMetadata === "string"
              ? (() => {
                  try {
                    return JSON.parse(post.postLinkMetadata);
                  } catch (e) {
                    return null;
                  }
                })()
              : post.postLinkMetadata;
          if (meta) {
            setPostLinkPreview(meta);
            // Seed locks so editing doesn't re-fetch unnecessarily
            firstLinkRef.current = post.postLink || null;
            lastFetchedLinkRef.current = post.postLink || null;
          }
        }
        setValue("hideLinkPreview", Boolean(post.hideLinkPreview));

        if (post?.media?.currBucketUrl) {
          dispatch(setMedia(post.media.id));
          setPrevFileType(post.media.fileType);
          setPrevFile(post.media.currBucketUrl);
          editPostFormData.current.append("mediaId", post?.media?.id);
        } else {
          dispatch(setMedia(""));
          dispatch(setFile(null));
          dispatch(setFileName(""));
          setPrevFile("");
          setPrevFileType("");
        }
      });
    }

    return () => {
      if (id) resetFile();
      dispatch(resetPost());
    };
  }, []);

  // ── Show event tab for group admins/moderators ────────────────────────────
  useEffect(() => {
    const checkGroupRole = async () => {
      try {
        if (isGroupOnlyPost && selectedGroups && selectedGroups.length === 1) {
          const gid = selectedGroups[0];

          const [groupResp, disabledResp] = await Promise.all([
            axios.get(`${GET_GROUP}/${gid}/1`),
            axios.get(`${CHECK_GROUP_DISABLED_FOR_EVENTS}/${gid}`),
          ]);

          const grp = groupResp.data.group || groupResp.data;
          const isAdmin =
            userRecord?.id === grp?.admin?.id || userRecord?.id === grp?.admin;
          const isModerator = grp?.moderators?.some(
            (m) => m?.id === userRecord?.id,
          );

          // ✅ FIX: actually set the disabled flag from the dedicated endpoint
          setIsGroupDisabledForEvents(disabledResp.data?.isDisabled === true);
          setShowEventTab(Boolean(isAdmin || isModerator));
        } else {
          setShowEventTab(false);
          setIsGroupDisabledForEvents(false); // ✅ reset when no group selected
        }
      } catch (err) {
        setShowEventTab(false);
        setIsGroupDisabledForEvents(false);
      }
    };
    checkGroupRole();
  }, [isGroupOnlyPost, selectedGroups, userRecord]);

  // ── Form submission ────────────────────────────────────────────────────────
  const onSubmit = async (data) => {
    if (!description || String(description).trim() === "") {
      setSubmitted(true);
      return;
    }
    let payloadLink = watch("postLink")?.trim();
    if (payloadLink) {
      payloadLink = extractUrl(payloadLink);
      if (payloadLink && !validator.isURL(payloadLink))
        return toast.error("Please enter a valid link");
    }

    if (id) {
      // EDIT
      try {
        if (watch("postLink") && !validator.isURL(watch("postLink")))
          return toast.error("Please enter a valid link");

        const formData = editPostFormData.current;
        if (!media && file) {
          setLoading(true);
          formData.append("file", file);
          await axios.post(UPLOAD_FILE, formData).then((res) => {
            if (res.data) {
              setLoading(false);
              formData.set("mediaId", res.data);
            } else {
              toast.error("Something went wrong while uploading media");
            }
          });
        }

        formData.append("description", description || "");
        formData.append("ageRestricted", Boolean(ageRestricted));
        if (
          payloadLink !== undefined &&
          payloadLink !== null &&
          String(payloadLink).trim() !== ""
        ) {
          formData.append("postLink", payloadLink);
        } else {
          formData.append("postLink", "");
        }
        formData.append("hideLinkPreview", watch("hideLinkPreview"));
        if (postLinkPreview)
          formData.append("postLinkMetadata", JSON.stringify(postLinkPreview));

        const res = await axios.post(`/post/edit/${id}`, formData);

        dispatch(resetPost());
        // Optimistically merge OG — server may not echo postLinkMetadata back
        dispatch(
          updatePost({
            ...res.data,
            postLinkMetadata:
              postLinkPreview || res.data.postLinkMetadata || null,
            hideLinkPreview: Boolean(watch("hideLinkPreview")),
          }),
        );
        const returnTo = location?.state?.returnTo;
        if (returnTo) {
          navigation(returnTo, { replace: true, state: { fromNotifications: true } });
        } else {
          // Prefer returning to the group from which edit was initiated
          const returnToGroupId =
            location?.state?.returnToGroupId ||
            (selectedGroups &&
              selectedGroups.length > 0 &&
              selectedGroups[0]) ||
            id;
          if (returnToGroupId)
            navigation(`/group/info/${returnToGroupId}`, { replace: true, state: { fromNotifications: true } });
          else navigation(ROOT, { replace: true });
        }
      } catch (error) {
        setLoading(false);
      }
    } else {
      // CREATE
      try {
        if (watch("postLink") && !validator.isURL(watch("postLink")))
          return toast.error("Please enter a valid link");

        let mediaFile = media;
        if (!mediaFile && file) {
          const formData = new FormData();
          setLoading(true);
          formData.append("file", file);
          await axios.post(UPLOAD_FILE, formData).then((res) => {
            if (res.data) {
              setLoading(false);
              dispatch(setMedia(res.data));
              mediaFile = res.data;
            } else {
              toast.error("Something went wrong while uploading media");
            }
          });
        }

        if (!Boolean(selectedPostCommunities?.length))
          return toast.error("Please select a community");

        await axios
          .post(CREATE_POST, {
            description: description,
            mediaId: mediaFile,
            connections: Array.from(connections),
            communities: Array.from(communities),
            groups: Array.from(selectedGroups),
            selectedPostCommunities: selectedPostCommunities,
            tags: selectedTags?.map((tag) => tag.id),
            ageRestricted: ageRestricted,
            postLink: payloadLink,
            postLinkMetadata: postLinkPreview || null,
            hideLinkPreview: watch("hideLinkPreview"),
          })
          .then((res) => {
            toast.success("Post created successfully");
            dispatch(resetPost());

            // Optimistically merge OG — server may not echo postLinkMetadata back
            dispatch(
              addToPosts({
                ...res.data,
                postLinkMetadata:
                  postLinkPreview || res.data.postLinkMetadata || null,
                hideLinkPreview: Boolean(watch("hideLinkPreview")),
              }),
            );

            try {
              const created = res.data || {};
              const groups = created.groups || [];
              if (Array.isArray(groups) && groups.length) {
                groups.forEach((g) => {
                  try {
                    const gid = typeof g === "object" ? g.id : g;
                    if (gid) localStorage.removeItem(`group-${gid}-cache`);
                  } catch (err) {}
                });
              }
              try {
                localStorage.setItem("stream-refresh", Date.now().toString());
                window.dispatchEvent(new Event("stream:refresh"));
              } catch (err) {}
            } catch (err) {}

            navigation(ROOT, { replace: true });
          });
      } catch (error) {
        setLoading(false);
      }
    }
  };

  const onFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      if (e.target.files[0].size > 45 * 1024 * 1024) {
        toast.error("File size should be less than 45MB");
        return;
      }
      setValue("file", e.target.files[0]);
      dispatch(setFileName(e.target.files[0].name));
      dispatch(setFile(e.target.files[0]));
      setPrevFile(URL.createObjectURL(e.target.files[0]));
      setPrevFileType(e.target.files[0].type.split("/")[0]);
    }
    if (e.target.value) e.target.value = null;
  };

  const resetFile = () => {
    setPrevFile("");
    setPrevFileType("");
    setValue("file", "");
    dispatch(setFileName(""));
    dispatch(setFile(""));
    dispatch(setMedia(""));
  };

  const handleSelectDescriptionEmoji = (emoji) => {
    const descArray = description.split("");
    descArray.splice(descCursorPos, 0, emoji);
    const newDesc = descArray.join("");
    dispatch(setDescription(newDesc));
  };

  useEffect(() => {
    if (!watch("hideLinkPreview") && watch("postLink")) handleEnterPostLink();
  }, [watch("hideLinkPreview")]);

  useEffect(() => {
    setPostLinkPreviewError("");
    if (watch("postLink")?.endsWith(" ")) handleEnterPostLink();
  }, [watch("postLink")]);

  return (
    <Layout>
      <Title
        onBack={() => {
          const returnTo = location?.state?.returnTo;
          if (returnTo) {
            navigation(returnTo, { replace: true });
          } else {
            const gid =
              (selectedGroups &&
                selectedGroups.length > 0 &&
                selectedGroups[0]) ||
              id;
            if (gid) navigation(`/group/info/${gid}`, { replace: true });
            else navigation(ROOT, { replace: true });
          }
        }}
      >
        Add to Stream
      </Title>
      <PostTypeTabs
        activeTab="standard"
        selectedGroups={selectedGroups}
        showEventTab={showEventTab}
        isGroupDisabledForEvents={isGroupDisabledForEvents}
      />
      <div className="m-auto flex w-5/6 flex-col gap-4">
        <form
          onSubmit={handleSubmit(onSubmit)}
          encType="multipart/form-data"
          className="flex flex-col gap-2 mb-[100px] pt-4"
          noValidate
        >
          {postLinkPreviewLoading || postLinkPreview ? (
            postLinkPreviewLoading ? (
              <Loader />
            ) : watch("hideLinkPreview") ? (
              <div className="flex justify-end">
                <Button
                  variant="contained"
                  onClick={() => setValue("hideLinkPreview", false)}
                >
                  Show link preview
                </Button>
              </div>
            ) : (
              <div className="relative">
                <OpenGraphInfo className="mb-4" data={postLinkPreview} />
                <Tooltip title="Remove link preview">
                  <CloseIcon
                    className="cursor-pointer absolute -top-2 -right-2"
                    onClick={() => setValue("hideLinkPreview", true)}
                  />
                </Tooltip>
              </div>
            )
          ) : (
            <div className="flex justify-center">
              <div
                className={clsx(
                  prevFile ? "" : classes.colorGreen,
                  prevFile ? "" : classes.borderGreen,
                  "my-5 flex relative flex-col items-center justify-center w-40 min-h-40 border-dashed p-4 rounded-full",
                  prevFile ? "w-full" : "border-dashed cursor-pointer",
                )}
                onClick={() => fileInput.current.click()}
              >
                {prevFile && (
                  <Typography
                    color={"red"}
                    onClick={(e) => {
                      e.stopPropagation();
                      editPostFormData.current.set("mediaId", "");
                      resetFile();
                    }}
                    className="absolute cursor-pointer top-5 left-5"
                  >
                    X
                  </Typography>
                )}

                <div>
                  <CardMedia
                    crossOrigin="anonymous"
                    component={"video"}
                    controls
                    className={clsx(
                      "mb-2 h-40",
                      prevFile && prevFileType === "video" ? "" : "hidden",
                    )}
                    image={prevFile}
                    alt="Stream Media"
                    muted
                  />
                  <img
                    src={prevFile}
                    alt="Upload"
                    className={clsx(
                      "mb-2 h-40 object-contain",
                      prevFile && prevFileType === "image" ? "" : "hidden",
                    )}
                  />
                </div>

                {!Boolean(prevFile) && (
                  <img
                    src={upload}
                    alt="Upload"
                    className="mb-2 object-contain"
                  />
                )}
                <Typography>{fileName ? fileName : "Add File"}</Typography>
                {!fileName ? (
                  <Typography fontSize={10} className="text-center">
                    (Maximum 45 MBs)
                  </Typography>
                ) : null}
                <input
                  className="hidden"
                  multiple={true}
                  type="file"
                  accept="image/*,video/*"
                  ref={fileInput}
                  onChange={onFileChange}
                />
              </div>
            </div>
          )}

          <div className="input-wrapper flex flex-col relative CreatePost">
            <MentionsInput
              value={description}
              style={{
                ...mentionsInputStyle,
                "&multiLine": {
                  ...mentionsInputStyle["&multiLine"],
                  input: {
                    ...mentionsInputStyle["&multiLine"].input,
                    borderColor: errors.description ? "#d32f2f" : "#887E7E",
                    borderWidth: errors.description ? 1 : 2,
                  },
                },
              }}
              className="h-32 rounded-md overflow-y-hidden noto-font"
              placeholder="Got something to share? Type it here"
              onSelectCapture={(e) => setDescCursorPos(e.target.selectionStart)}
              onChange={(e) => dispatch(setDescription(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter")
                  tryFetchOG(e.target.value);
              }}
              onBlur={(e) => tryFetchOG(e.target.value)}
            >
              <Mention
                trigger="@"
                style={mentionStyle}
                key={mentionSuggestionsLoading}
                isLoading={mentionSuggestionsLoading}
                data={mentionSuggestions?.map((user) => ({
                  id: user.userName,
                  display: user.userName,
                }))}
                appendSpaceOnAdd={true}
                renderSuggestion={(
                  suggestion,
                  search,
                  value,
                  index,
                  focused,
                ) => (
                  <MyAvatar
                    name={value}
                    src={
                      mentionSuggestions?.find(
                        (user) => user.userName === suggestion.id,
                      )?.media?.currBucketUrl
                    }
                  />
                )}
              />
            </MentionsInput>

            <EmojiSelector
              positionClassName="bottom-1 right-1"
              onSelection={handleSelectDescriptionEmoji}
            />
          </div>
          {errors.description && (
            <p className="text-xs italic text-red-500 mt-1">
              {errors.description.message || "Description is required."}
            </p>
          )}
          {postLinkPreviewError && (
            <p className="text-xs italic text-red-500 mt-1">
              {postLinkPreviewError}
            </p>
          )}
          {userRecord?.isMinor == false && (
            <FormControl>
              <FormControlLabel
                label={`This content is not suitable for individuals under ${process.env.REACT_APP_MINOR_AGE} years of age`}
                control={
                  <Checkbox
                    checked={ageRestricted}
                    onChange={(e) =>
                      dispatch(setAgeRestricted(e.target.checked))
                    }
                  />
                }
              />
            </FormControl>
          )}

          {!id && (
            <div className="mt-4">
              <AddComCon open={showComCon} />
            </div>
          )}

          <div className="z-10 fixed bottom-0 left-0 w-full flex justify-center border-t">
            <div className="bg-white w-5/6 m-auto md:w-1/2 py-4 md:px-10 flex justify-end">
              <button
                type="submit"
                onClick={() => {
                  setSubmitted(true);
                }}
                className={clsx(classes.button, "w-24 rounded-3xl p-2 px-6")}
              >
                {loading ? (
                  <CircularProgress size={"20px"} color="inherit" />
                ) : (
                  <Typography color={"white"}>
                    {id ? "Update" : "Post"}
                  </Typography>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreatePost;
